import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { AppSvgIconComponent, State } from '@vallift/ngl';
import type { GetDataLibraryItemApiResponse } from '../../models/get-data-library-item';
import { DataLibraryItemApiService } from '../../services/data-library-item-api.service';

export interface EntityRecordPreviewOverlayData {
  itemId: string;
  display?: string;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

@Component({
  selector: 'app-entity-record-preview-overlay',
  standalone: true,
  imports: [AppSvgIconComponent],
  templateUrl: './entity-record-preview-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '(mouseenter)': 'onCardEnter()',
    '(mouseleave)': 'onCardLeave()',
  },
})
export class EntityRecordPreviewOverlayComponent implements OnInit {
  private readonly data = inject<EntityRecordPreviewOverlayData>(DIALOG_DATA);
  private readonly api = inject(DataLibraryItemApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loadState = new State<GetDataLibraryItemApiResponse>();
  readonly fallbackDisplay = signal(this.data.display?.trim() || '');

  ngOnInit(): void {
    this.loadState.notifyInitialLoading();
    this.api
      .getItem(this.data.itemId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.loadState.notifySuccess(response),
        error: () =>
          this.loadState.notifyError('Could not load this record.'),
      });
  }

  onCardEnter(): void {
    this.data.onPointerEnter?.();
  }

  onCardLeave(): void {
    this.data.onPointerLeave?.();
  }

  collectionName(): string {
    return (
      this.loadState.response()?.data.collection.name?.trim() || 'Collection'
    );
  }

  fields(): Array<{ key: string; name: string; value: string }> {
    const payload = this.loadState.response()?.data;
    if (!payload) {
      return [];
    }
    return (payload.fields ?? []).map((field) => ({
      key: field.key,
      name: field.name,
      value: formatFieldValue(payload.item.values?.[field.key]),
    }));
  }

  isLoading(): boolean {
    return this.loadState.loading();
  }

  hasError(): boolean {
    return this.loadState.failed();
  }
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value.trim() || '—';
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
